import { useEffect, useState } from "react";
import * as React from "react";

import {
  Button,
  FormHelperText,
  Grid,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  DialogActions,
  Autocomplete,
  Typography,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from "@mui/material";

import * as Yup from "yup";

import { openSnackbar } from "../../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../../api";
import { SnackbarProps } from "../../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormik, FormikProvider, Form } from "formik";

import { PROFICIENCY_LEVELS } from "./mockData";
import {
  Language,
  ResumeLanguage,
} from "../../../../../../types/resumes-managment/resume";
import {
  useLanguages,
  useLanguagesForAutocomplete,
} from "../../../../../../hooks/query-hooks/useLanguages";

const getInitialValues = (experience: ResumeLanguage | null) => {
  if (experience && experience.id) {
    return {
      language_id: experience.language_id || null,
      reading_level: experience.reading_level || "",
      conversation_level: experience.conversation_level || "",
      writing_level: experience.writing_level || "",
    };
  } else {
    return {
      language_id: null,
      reading_level: "",
      conversation_level: "",
      writing_level: "",
    };
  }
};

const LanguageSchema = Yup.object().shape({
  language_id: Yup.number().required("Language is required"),
  reading_level: Yup.string().required("Reading level is required"),
  conversation_level: Yup.string().required("Conversation level is required"),
  writing_level: Yup.string().required("Writing level is required"),
});

function AddEditResumeLanguageForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openLanguage: boolean) => void;
  item: ResumeLanguage | null;
}) {
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>(
    {}
  );
  const [languageSearchTerm, setLanguageSearchTerm] = useState("");
  const {
    items: languages,
    loading: loadingLanguages,
    hasMore: hasMoreLanguages,
    loadMore: loadMoreLanguages,
    error: languagesError,
  } = useLanguagesForAutocomplete(20, languageSearchTerm);

  const theme = useTheme();

  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      ...getInitialValues(item ?? null),
    },
    validationSchema: LanguageSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();

        formData.append("resume_id", resumeId.toString());
        formData.append("language_id", values.language_id?.toString() || "");
        formData.append("reading_level", values.reading_level);
        formData.append("conversation_level", values.conversation_level);
        formData.append("writing_level", values.writing_level);

        if (item && item?.id) {
          const response = await USERAXIOS.post(
            `resume/languages/${item?.id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          const updatedLanguage = response.data.data;

          if (updatedLanguage) {
            setResult((prev: ResumeLanguage[]) =>
              prev.map((item) =>
                item.id === updatedLanguage.id ? updatedLanguage : item
              )
            );

            openSnackbar({
              open: true,
              message: "Language updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post("resume/languages", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          const newLanguage = response.data.data;

          if (newLanguage) {
            setResult((prev: ResumeLanguage[]) => [...prev, newLanguage]);

            openSnackbar({
              open: true,
              message: "Language added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        setServerErrors(error.response.data.message.message);
        console.error(
          "Error submitting form:",
          error.response.data.message.message
        );
        openSnackbar({
          open: true,
          message: "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, setFieldValue, values } =
    formik;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Language" />
        ) : (
          <FormattedMessage id="Add Language" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Language" />
                  </Typography>
                  <Autocomplete
                    value={
                      languages?.find(
                        (language) => language.id === values.language_id
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue("language_id", newValue?.id || null);
                      // Clear search term when option is selected
                      if (newValue) {
                        setLanguageSearchTerm("");
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setLanguageSearchTerm(newInputValue);
                      }
                    }}
                    options={languages}
                    getOptionLabel={(option: Language) =>
                      theme.direction === "rtl"
                        ? option.name_trans.ar
                        : option.name
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id=" Languages" />}
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingLanguages && (
                                <CircularProgress color="inherit" size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        const threshold = 100;
                        if (
                          listboxNode.scrollHeight - listboxNode.scrollTop <=
                            listboxNode.clientHeight + threshold &&
                          hasMoreLanguages &&
                          !loadingLanguages
                        ) {
                          loadMoreLanguages();
                        }
                      },
                    }}
                    loading={loadingLanguages}
                    filterOptions={(x) => x} // Disable built-in filtering
                    noOptionsText={
                      languageSearchTerm.length >= 2
                        ? "No languages found"
                        : "Type to search languages"
                    }
                  />
                  {languagesError && (
                    <FormHelperText error>
                      Error loading languages. Please try again.
                    </FormHelperText>
                  )}

                  {touched.language_id && errors.language_id && (
                    <FormHelperText error>{errors.language_id}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Reading Level" />
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={values.reading_level}
                      onChange={(e) =>
                        setFieldValue("reading_level", e.target.value)
                      }
                      error={
                        touched.reading_level && Boolean(errors.reading_level)
                      }
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select reading level
                      </MenuItem>
                      {PROFICIENCY_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {theme.direction === "rtl"
                            ? level?.arabic_label
                            : level?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.reading_level && errors.reading_level && (
                    <FormHelperText error>
                      {errors.reading_level}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Conversation Level" />
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={values.conversation_level}
                      onChange={(e) =>
                        setFieldValue("conversation_level", e.target.value)
                      }
                      error={
                        touched.conversation_level &&
                        Boolean(errors.conversation_level)
                      }
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select conversation level
                      </MenuItem>
                      {PROFICIENCY_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {theme.direction === "rtl"
                            ? level?.arabic_label
                            : level?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.conversation_level && errors.conversation_level && (
                    <FormHelperText error>
                      {errors.conversation_level}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <Typography>
                    <FormattedMessage id="Writing Level" />
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={values.writing_level}
                      onChange={(e) =>
                        setFieldValue("writing_level", e.target.value)
                      }
                      error={
                        touched.writing_level && Boolean(errors.writing_level)
                      }
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select writing level
                      </MenuItem>
                      {PROFICIENCY_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {theme.direction === "rtl"
                            ? level?.arabic_label
                            : level?.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.writing_level && errors.writing_level && (
                    <FormHelperText error>
                      {errors.writing_level}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditResumeLanguageForm;
